import { isDefined } from "@clipboard-health/util-ts";
import { useFacilityProfile } from "@src/appV2/Facilities/api/useFacilityProfile";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { useMemo } from "react";

import { DEFAULT_REFERRAL_POST_CLOCK_OUT_CTA_CONFIG } from "./constants";

interface UseReferralPostClockOutCtaParams {
  workplaceId: string;
  workplaceType: string | undefined;
}

interface UseReferralPostClockOutCtaResult {
  isEligible: boolean;
  msa: string | undefined;
}

export function useReferralPostClockOutCta(
  params: UseReferralPostClockOutCtaParams
): UseReferralPostClockOutCtaResult {
  const { workplaceId, workplaceType } = params;

  const config = useCbhFlag(CbhFeatureFlag.REFERRAL_POST_CLOCK_OUT_CTA, {
    defaultValue: DEFAULT_REFERRAL_POST_CLOCK_OUT_CTA_CONFIG,
  });

  const { data: facilityProfile } = useFacilityProfile<"pricingMsa">(
    {
      workplaceId,
      projection: "pricingMsa",
    },
    { enabled: config.enabled && workplaceId.length > 0 }
  );

  const msa = facilityProfile?.pricingMsa ?? undefined;

  const isEligible = useMemo(() => {
    if (!config.enabled) {
      return false;
    }

    const { workplaceTypes, msas } = config;

    if (isDefined(workplaceTypes)) {
      if (workplaceTypes.length === 0) {
        return false;
      }

      if (!isDefined(workplaceType) || !workplaceTypes.includes(workplaceType)) {
        return false;
      }
    }

    if (isDefined(msas)) {
      if (msas.length === 0) {
        return false;
      }

      if (!isDefined(msa) || !msas.includes(msa)) {
        return false;
      }
    }

    return true;
  }, [config, workplaceType, msa]);

  return { isEligible, msa };
}
